import { useCallback, useEffect, useState } from 'react';

import { ApiError, apiRequest } from '../../api/client.js';
import type { BotExecution } from '../../api/types.js';
import { Alert } from '../../components/Alert.js';
import { EmptyState } from '../../components/EmptyState.js';
import { PlatformIcon } from '../../components/PlatformIcon.js';
import { StatusPill, type StatusTone } from '../../components/StatusPill.js';

/**
 * What the bot has done (§102, module brief §43).
 *
 * ==================== SIMULATIONS ARE SHOWN, NOT HIDDEN ====================
 * The default filter is "everything", including simulations, and each row says
 * which it is. Hiding them by default would make the Test Console's output
 * invisible to the operator who just used it; showing them without a label
 * would let a simulation be read as evidence a message went out. Both are
 * failures, and the label plus the filter is what avoids each.
 * ==========================================================================
 */

/**
 * Outcome → tone, in one place.
 *
 * §55: status colour is mapped centrally rather than decided per row. Only
 * `DISPATCHED` is a success; a cooldown or a role denial is the bot working as
 * configured, which is neither good news nor bad, and `ERROR` is the only one
 * that is Connaro's own fault.
 */
function toneFor(outcome: BotExecution['outcome']): StatusTone {
  if (outcome === 'DISPATCHED') return 'ok';
  if (outcome === 'ERROR') return 'error';
  if (outcome === 'ROLE_UNAVAILABLE' || outcome === 'NO_TARGET') return 'warn';
  return 'idle';
}

/** The enum, in words an operator did not have to learn. */
const OUTCOME_LABELS: Record<BotExecution['outcome'], string> = {
  DISPATCHED: 'Dispatched',
  COOLDOWN: 'Cooling down',
  ROLE_DENIED: 'Not allowed',
  ROLE_UNAVAILABLE: 'Role unavailable',
  DISABLED: 'Skipped',
  NO_TARGET: 'No target',
  LOOP_PREVENTED: 'Loop prevented',
  NOT_ENTITLED: 'Not on plan',
  ERROR: 'Error',
};

function labelFor(outcome: BotExecution['outcome']): string {
  return OUTCOME_LABELS[outcome];
}

const FILTERS = [
  { value: '', label: 'Everything' },
  { value: 'simulated=false', label: 'Real runs' },
  { value: 'simulated=true', label: 'Simulations' },
] as const;

export function BotLogs(): React.JSX.Element {
  const [filter, setFilter] = useState('');
  const [rows, setRows] = useState<BotExecution[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const data = await apiRequest<{ executions: BotExecution[] }>(
        `/bot/logs${filter === '' ? '' : `?${filter}`}`,
      );
      setRows(data.executions);
    } catch (caught) {
      setRows([]);
      setError(caught instanceof ApiError ? caught.message : 'Connaro could not read the log.');
    }
  }, [filter]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="bot__section">
      {error !== null && <Alert tone="error">{error}</Alert>}

      <div className="composer__field bot__prefix">
        <label className="field__label" htmlFor="log-filter">
          Show
        </label>
        <select
          id="log-filter"
          className="field__input"
          value={filter}
          onChange={(event) => {
            setFilter(event.target.value);
          }}
        >
          {FILTERS.map((entry) => (
            <option key={entry.value} value={entry.value}>
              {entry.label}
            </option>
          ))}
        </select>
      </div>

      {rows === null ? (
        <p className="field__hint">Loading…</p>
      ) : rows.length === 0 ? (
        <EmptyState title="No activity yet">The bot records every command, timer and automatic reply here, including simulations.</EmptyState>
      ) : (
        <ul className="bot__list">
          {rows.map((row) => (
            <li className="bot__item" key={row.id}>
              <div className="bot__item-head">
                {/*
                 * The mark makes a mixed-platform log scannable. A timer has
                 * no originating platform, so nothing is drawn rather than a
                 * placeholder implying one.
                 */}
                {row.platform !== null && (
                  <PlatformIcon
                    platform={row.platform}
                    displayName={row.platform}
                    size={16}
                    label={row.platform}
                  />
                )}
                <span className="bot__item-name">{row.sourceLabel}</span>
                {/*
                 * The outcome as a WORD. `DISPATCHED` is deliberately not
                 * "Sent": whether the audience saw it is the per-target answer
                 * this row carries, and a single word for a fan-out is wrong
                 * about at least one platform.
                 */}
                <StatusPill tone={toneFor(row.outcome)} label={labelFor(row.outcome)} />
                {row.simulated && <StatusPill tone="warn" label="Simulation" />}
              </div>

              <span className="field__hint">
                {new Date(row.occurredAt).toLocaleString()}
                {row.platform === null ? '' : ` · ${row.platform}`}
                {row.actorDisplayName === null ? '' : ` · ${row.actorDisplayName}`}
              </span>

              {row.reason !== null && <span className="bot__item-detail">{row.reason}</span>}
              {row.renderedPreview !== null && (
                <span className="bot__item-detail">{row.renderedPreview}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
